import {TranslateService} from '@ngx-translate/core';
import {AfterViewInit, OnDestroy, OnInit, ViewChild, Directive} from '@angular/core';
import {Store} from '@ngxs/store';
import * as _ from 'lodash';
import {DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS, SERVER_API_URL} from 'app/app.constants';
import {AbstractEntityResource} from 'app/blocks/resource/abstract-entity-resource';
import {EntityDataSource} from 'app/blocks/util/entity-datasource';
import {buildListApiParams, ListViewParams} from 'app/blocks/util/list-view-params';
import {DiDate} from 'app/blocks/util/di-date';
import {AlertService} from 'app/common/alert/alert.service';
import {DialogService} from 'app/common/dialog/dialog-service';
import {NzPaginationComponent} from 'ng-zorro-antd/pagination';
import {NzTableComponent} from 'ng-zorro-antd/table';
import {BehaviorSubject, combineLatest, merge, Observable, of, Subject} from 'rxjs';
import {debounceTime, takeUntil, tap, map} from 'rxjs/operators';

import {
    ChangeEntityListViewParams,
    DeleteEntity,
    GetEntityList,
    SelectAllCurrentPageEntities,
    SelectEntity,
    UnselectAllCurrentPageEntities,
    UnselectEntity
} from 'app/blocks/store/actions/entity-actions';
import {IBaseEntity} from 'app/blocks/model/base-entity.model';
import {EntityListHeaderParams} from 'app/blocks/entity/entity-list-header-params';
import {EntityListMasterCheckboxParams} from 'app/blocks/entity/entity-list-master-checkbox-params';
import {DiListPageHeaderComponent} from 'app/common/di-list-page-header/di-list-page-header.component';
import {DEBUG_PAGE_PARAMS} from 'app/app.constants';
import {FilterSpec} from 'app/blocks/util/filter-spec';
import {FilterParams} from 'app/blocks/util/filter-params';
import {ExportService} from 'app/blocks/service/api/export.service';
import {
    ExportSettingsModalComponent,
    ExportSettingsModalData
} from 'app/common/export-settings/export-settings-modal/export-settings-modal.component';
import {NzModalService} from 'ng-zorro-antd/modal';
import {AccountService} from 'app/core/auth/account.service';
import {OrderStatus} from 'app/blocks/model/haulier-order.model';
import {CsvBoxUtilsService} from 'app/common/utils/csvbox-utils-service';
import {TitleService} from 'app/blocks/service/title.service';
import {ImportResult} from 'app/main/private/setup/import-data/model/import-result.model';
import {HttpClient} from '@angular/common/http';
import {ImportCsvResultModalComponent} from 'app/common/import-csv-result-modal/import-csv-result-modal.component';
import {ImportEntity} from 'app/blocks/model/enums/import-entity.enum';
import {IntegrationUtils} from 'app/common/utils/integration-utils';
import {PagingService} from 'app/blocks/service/paging.service';
import {AIInputService, ScreenType} from 'app/blocks/service/ai-input.service';
import {EntityAction} from 'app/blocks/model/action.model';
import {AIResource} from 'app/constants/ai';
import {trackAssistantScreen} from 'app/blocks/service/assistant-context/assistant-context.service';

declare let CSVBoxImporter: any;

type NzSortDirection = 'ascend' | 'descend' | null;

export interface FilterParamMap {
    [key: string]: FilterParams;
}

@Directive({
    standalone: false
})
// tslint:disable-next-line:directive-class-suffix
export abstract class EntityListComponent<T extends IBaseEntity> implements OnInit, OnDestroy, AfterViewInit {
    readonly viewParams$: Observable<ListViewParams>;
    readonly totalItems$: Observable<number>;
    readonly pageSize$: Observable<number>;
    readonly pageIndex$: Observable<number>;
    readonly entityList$: Observable<T[]>;
    readonly entityListSaving$: Observable<boolean>;
    readonly selectedIds$: Observable<number[]>;
    readonly someIdsSelected$: Observable<boolean> = of(false);
    readonly masterCheckboxStatus$: Observable<any>;

    readonly dataSource: EntityDataSource<T>;
    readonly defaultPageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
    readonly tableStyle = {y: 'calc(100vh - 240px)'};
    readonly showSizeChanger = true;
    showCheckbox = false;
    readonly tableSize = 'small';
    readonly showQuickJumper = true;
    readonly borderedTable = false;
    readonly csvBoxLicenceKey: string = '';

    public OrderStatusEnum = OrderStatus;

    filterSpec: FilterSpec;
    filterParamMap: FilterParamMap = {};
    ngFilterModel: {[key: string]: any} = {};
    readonly filterParamsSubject: BehaviorSubject<FilterParams[]> = new BehaviorSubject<FilterParams[]>([]);
    readonly filterParams$: Observable<FilterParams[]> = this.filterParamsSubject.asObservable();
    readonly activeFilterCountSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    readonly activeFilterCountParams$: Observable<number> = this.activeFilterCountSubject.asObservable();
    public _groupActionSubject = new BehaviorSubject(false);
    groupAction$ = this._groupActionSubject.asObservable();

    loading = false;
    selectedIds = [];
    resourceName: string = this.resource.name;

    readonly VIEW_ACTION = {
        label: 'view',
        icon: 'remove_red_eye',
        action: this.viewEntity.bind(this),
        enabled$: of(true)
    };

    readonly IMPORT_PDF = {
        label: 'importPdf',
        icon: 'description',
        action: this.importPdf.bind(this),
        enabled$: of(true)
    };

    readonly EDIT_ACTION = {
        label: 'edit',
        icon: 'edit',
        action: this.editEntity.bind(this),
        enabled$: of(true)
    };

    readonly DELETE_ACTION = {
        label: 'delete',
        icon: 'delete',
        action: this.deleteEntity.bind(this)
    };

    readonly DELETE_SELECTED_ACTION = {
        label: 'deleteSelected',
        icon: 'delete',
        action: this.deleteSelectedEntities.bind(this),
        enabled$: this.someIdsSelected$
    };

    EXPORT_ALL = {
        label: 'exportAll',
        dynamicValue: 0,
        icon: 'file_copy',
        action: this.exportAll.bind(this),
        enabled$: of(true)
    };

    EXPORT_SELECTED = {
        label: 'exportSelected',
        dynamicValue: 0,
        icon: 'file_copy',
        action: this.exportSelected.bind(this),
        enabled$: of(true)
    };

    readonly IMPORT_CSV_ACTION = {
        label: 'importCsv',
        icon: 'description',
        action: this.importCsv.bind(this),
        enabled$: of(true)
    };

    readonly EXPORT_SELECTED_TO_ACCOUNTING_PACKAGE = {
        label: 'exportToAccountingPackage',
        icon: 'description',
        action: this.exportSelectedToAccountingPackage.bind(this, true),
        enabled$: this.someIdsSelected$
    };

    @ViewChild(DiListPageHeaderComponent, {static: false})
    nzHeader: DiListPageHeaderComponent;

    @ViewChild(NzTableComponent, {static: false})
    nzTable: NzTableComponent<T>;

    @ViewChild(NzPaginationComponent, {static: true})
    nzPagination: NzPaginationComponent;

    headerParams;
    entityActions: any[];
    masterCheckboxActions: EntityListMasterCheckboxParams;

    sortName = 'id';
    sortDir: NzSortDirection = 'descend';
    sortParams: {[key: string]: NzSortDirection} = {id: 'descend'};

    protected _unsubscribeAll = new Subject<void>();

    exportCount = 1;
    exportType = '';

    readonly CHANGE_EXPORT_SETTINGS = {
        label: 'changeExportSettings',
        icon: 'description',
        action: this.changeExportSettings.bind(this),
        enabled$: of(true)
    };

    private importer: any;
    public aiActions: EntityAction[] = [];

    constructor(
        protected _store: Store,
        protected _dialogService: DialogService,
        protected _alertService: AlertService,
        public readonly resource: AbstractEntityResource<T>,
        protected titleService: TitleService,
        public _translateService: TranslateService,
        private pagingService: PagingService,
        public _exportService?: ExportService,
        public _modalService?: NzModalService,
        protected accountService?: AccountService,
        protected csvBoxUtilsService?: CsvBoxUtilsService,
        protected httpClient?: HttpClient,
        protected aIInputService?: AIInputService
    ) {
        this.initializeSortData();
        this.entityList$ = this._store.select((state) => state.entities[this.resourceName].listView.entityList);
        this.entityListSaving$ = this._store.select((state) => state.entities[this.resourceName].listView.statusSaving);
        this.viewParams$ = this._store.select((state) => state.entities[this.resourceName].listView.viewParams);
        this.totalItems$ = this._store.select((state) => state.entities[this.resourceName].listView.totalItems);
        this.pageIndex$ = this._store.select((state) => state.entities[this.resourceName].listView.viewParams.pageIndex);
        this.pageSize$ = of(this.pagingService.getPageSize(this.resourceName));
        this.selectedIds$ = this._store.select((state) => state.entities[this.resourceName].listView.selectedIds);
        this.someIdsSelected$ = this._store
            .select((state) => state.entities[this.resourceName].listView.selectedIds)
            .pipe(map((ids) => ids.length > 0));
        this.masterCheckboxStatus$ = this.getMasterCheckboxStatus();
        this.dataSource = new EntityDataSource(this.entityList$);
        this.totalItems$.pipe().subscribe((totalItems) => {
            this.exportCount = this.EXPORT_ALL.dynamicValue = totalItems > 2000 ? 2000 : totalItems;
        });
        this.exportType = this.getExportType();
        // Assistant screen context: filters, search, sort, page and selected ids, read lazily from the store.
        trackAssistantScreen('list', this.resourceName, {list: () => this._store.selectSnapshot((s) => s.entities[this.resourceName]?.listView)});
    }

    initializeSortData(): void {
        // to be overridden in child classes
    }

    ngOnInit(): void {
        this._translateService
            .get(`${this.resource.name}.pages.list.heading`)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((heading) => {
                this.titleService.setTitle(heading);
            });
        this.headerParams = {
            icon: this.resource.icon,
            resource: this.resource,
            groupActions: this.getEntityGroupActions(),
            showAddButton: this.showAddButton(),
            addAction: () => this.resource.navigate.toNew(),
            groupActionProcessing: this.groupAction$,
            showRefresh: false,
            refreshAction: () => this.loadEntityList()
        } as EntityListHeaderParams;

        this.masterCheckboxActions = {
            resource: this.resource,
            selectActions: this.getMasterCheckboxActions()
        } as EntityListMasterCheckboxParams;

        this.entityActions = this.getEntityActions();
        this.filterSpec = this.getFilterSpec();
        this.subscribeToStatusLoadingChangeFromState();
        this.initializeFilterData();
        this.loadEntityList();

        if (this.isImportable()) {
            this.importer = this.csvBoxUtilsService.getCsvBoxImporter(
                this.csvBoxLicenceKey,
                {},
                this.resource.name,
                this.onCsvUploadComplete.bind(this)
            );
        }

        this.EXPORT_SELECTED_TO_ACCOUNTING_PACKAGE.enabled$ = this.someIdsSelected$;
        this.DELETE_SELECTED_ACTION.enabled$ = this.someIdsSelected$;
    }

    showAddButton(): boolean {
        return true;
    }

    loadEntityList(): void {
        this.beforeGetEntityList().then(() => {
            this._store.dispatch(new GetEntityList(this.resource, null, this.getViewParams(), this.getPrimaryFilter(), this.resourceName));
        });
    }

    // added so we can render data BEFORE we fire the initial call to get the entity list
    beforeGetEntityList(): Promise<any> {
        return new Promise((resolve) => {
            resolve(true);
        });
    }

    getFilterSpec(): FilterSpec {
        return {};
    }

    getViewParams(): ListViewParams {
        return null;
    }

    // tslint:disable-next-line:max-line-length
    // override this when integrating with multi page entities, this sets the default 'primaryFilter' filter on any and all getEntityList requests (excluding those made from a dropdown perspective)
    getPrimaryFilter(): string {
        return null;
    }

    loadFiltersIntoMap(filterParams: FilterParams[]): void {
        this.filterParamMap = {};

        if (filterParams) {
            for (const filterParam of filterParams) {
                if (filterParam.currentValue) {
                    if (filterParam.columnName === 'dateFromFilter' || filterParam.columnName === 'dateToFilter') {
                        this.filterParamMap[filterParam.columnName] = {
                            columnName: filterParam.columnName,
                            currentValue: filterParam.currentValue
                        };
                    } else {
                        this.filterParamMap[filterParam.columnName] = {
                            columnName: filterParam.columnName,
                            currentValue: filterParam.currentValue
                        };

                        if (!filterParam.extra && Array.isArray(filterParam.currentValue)) {
                            this.ngFilterModel[filterParam.columnName].forEach((e) => {
                                e.byDefault = filterParam.currentValue.includes(e.value);
                            });
                        } else if (filterParam.extra) {
                            const keys = Object.keys(filterParam.extra);
                            this.ngFilterModel[filterParam.columnName] = {
                                id: filterParam.currentValue
                            };
                            keys.forEach((key) => {
                                this.ngFilterModel[filterParam.columnName] = {
                                    ...this.ngFilterModel[filterParam.columnName],
                                    [key]: filterParam.extra[key]
                                };
                            });
                        }
                    }
                }
            }
        }
        this.activeFilterCountSubject.next(Object.keys(this.filterParamMap).length);
    }

    initializeFilterData(): void {
        this.log(`Initializing filter data...`);
        if (this.filterSpec) {
            for (const filterColumn of Object.keys(this.filterSpec)) {
                const spec = this.filterSpec[filterColumn];
                let initialValue = null;
                switch (spec.filterType) {
                    case 'checkbox':
                        initialValue = [];
                        if (spec.objectPath) {
                            const object = this._translateService.instant(spec.objectPath);
                            // translation is not available at the moment
                            if (object !== spec.objectPath) {
                                for (const key of Object.keys(object)) {
                                    initialValue.push({
                                        text: object[key],
                                        value: key,
                                        byDefault: spec?.defaultCheckboxValues?.[key] ?? false
                                    });
                                }
                            }
                        }
                        break;
                    case 'date-range':
                        initialValue = [];
                        break;
                    case 'search':
                        initialValue = null;
                        break;
                }
                this.ngFilterModel[filterColumn] = initialValue;
            }
        }
        this.log(`Initialized filter data with ${JSON.stringify(this.ngFilterModel)}`);
    }

    clearFilters(): void {
        this.log(`Clearing filters`);
        this.initializeFilterData();
        this.filterParamMap = {};
        this.activeFilterCountSubject.next(0);
        this.filterParamsSubject.next(Object.values(this.filterParamMap));
    }

    updateFilter(columnName: string, value: any): void {
        try {
            this.log(`Update filters for ${columnName} with ${JSON.stringify(value)}`);
            if (columnName && this.filterSpec[columnName]) {
                const filterSpec = this.filterSpec[columnName];
                const filterType = filterSpec.filterType;
                let currentValue: any;
                let extra = null;
                switch (filterType) {
                    case 'date-range':
                        if (value && value[0] && value[1]) {
                            currentValue = [DiDate.newInstance(value[0]), DiDate.newInstance(value[1])];
                        }
                        break;
                    case 'single-date':
                        if (value) {
                            currentValue = DiDate.newInstance(value);
                        } else {
                            currentValue = null;
                        }
                        break;
                    case 'checkbox':
                        if (!value || value.length === 0) {
                            currentValue = null;
                        } else {
                            currentValue = value;
                        }
                        break;
                    case 'custom':
                        if (!value || value.length === 0) {
                            currentValue = null;
                        } else {
                            currentValue = value;
                        }
                        break;
                    case 'search':
                        if (value && ((filterSpec.searchValueAttribute && value[filterSpec.searchValueAttribute]) || value.id)) {
                            currentValue = filterSpec.searchValueAttribute ? value[filterSpec.searchValueAttribute] : value.id;
                            if (!filterSpec.searchDisplayAttribute) {
                                throw new Error('FilterSpec for search should specify a display attribute!');
                            }
                            const displayAttribute = value[filterSpec.searchDisplayAttribute];
                            extra = {
                                [filterSpec.searchDisplayAttribute]: displayAttribute
                            };
                        } else if (value) {
                            currentValue = value;
                        }
                        break;
                    case 'empty-search':
                        currentValue = value;
                        break;
                    case 'string':
                        if (value) {
                            currentValue = value;
                        }
                        break;
                    default:
                        throw new Error(`Unrecognized filter type ${filterType}`);
                }
                if (currentValue) {
                    if (extra) {
                        this.filterParamMap[columnName] = {
                            columnName,
                            currentValue,
                            extra
                        };
                    } else {
                        this.filterParamMap[columnName] = {
                            columnName,
                            currentValue
                        };
                    }
                } else {
                    if (this.filterParamMap[columnName]) {
                        delete this.filterParamMap[columnName];
                    }
                }
                this.filterParamsSubject.next(Object.values(this.filterParamMap));
                this.activeFilterCountSubject.next(Object.keys(this.filterParamMap).length);
            } else {
                throw new Error(`${columnName} is not part of the FilterSpec! Will be ignored!`);
            }
        } catch (e) {
            // unknown filter column: ignored (see the error above)
        }
    }

    getNgFilterModelValue(filterParam: FilterParams): any {
        let newValue = null;
        // let filterActive = false;
        if (filterParam) {
            const columnName = filterParam.columnName;
            const filterSpec = this.filterSpec[columnName];
            const currentValue = filterParam.currentValue;
            switch (filterSpec.filterType) {
                case 'date-range':
                    if (currentValue && currentValue.length > 1) {
                        newValue = [DiDate.newInstance(currentValue[0]).asDate(), DiDate.newInstance(currentValue[1]).asDate()];
                        // filterActive = true;
                    } else {
                        newValue = [];
                    }
                    break;
                case 'checkbox':
                    newValue = [];
                    if (currentValue && currentValue.length > 0) {
                        const object = this._translateService.instant(filterSpec.objectPath);
                        for (const key of Object.keys(object)) {
                            const currentlySelected = currentValue.includes(key);
                            // filterActive = filterActive || currentlySelected;
                            newValue.push({
                                text: object[key],
                                value: key,
                                byDefault: currentlySelected
                            });
                        }
                    }
                    break;
                case 'search':
                    if (currentValue) {
                        newValue = {id: currentValue, ...filterParam.extra};
                    } else {
                        newValue = null;
                    }
                    break;
                default:
                    throw new Error(`Unsupported filter type for getting filter value! ${filterSpec.filterType}`);
            }
        }
        this.log(`Returning ngFilterModelValue ${JSON.stringify(newValue)}`);
        return newValue;
    }

    ngOnDestroy(): void {
        const reqData = {
            pageIndex: this.nzPagination.nzPageIndex - 1 || 0,
            pageSize: this.nzPagination.nzPageSize || this.pagingService.getPageSize(this.resourceName) || DEFAULT_PAGE_SIZE,
            sortColumn: this.sortName,
            sortDirection: 'ascend' === this.sortDir ? 'asc' : 'desc',
            filter: this.nzHeader && this.nzHeader.search ? this.nzHeader.search.getCurrentSearchTerm() || '' : '',
            filterParams: [],
            primaryFilter: this.getPrimaryFilter()
        } as ListViewParams;
        this._store.dispatch(new ChangeEntityListViewParams(this.resource, reqData, null, this.getPrimaryFilter(), this.resourceName));
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    ngAfterViewInit(): void {
        this.subscribeToViewParamsChangeFromState();
        if (this.nzPagination) {
            this.directNzPaginationViewParamsChangesToState();
        } else if (this.nzTable) {
            this.directNzViewParamsChangesToState();
        }
        this.afterViewLoaded();
        this.headerParams.groupActions.map(
            (action) =>
                new EntityAction({
                    label: action.label,
                    icon: action.icon,
                    action: action.action,
                    isEnabledFor: action.enabled$,
                    resourceName: this.resource.name
                })
        );
        this.aIInputService?.setData(this.resource.name, this.aiActions.concat(), ScreenType.LIST);
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    afterViewLoaded() {}

    viewEntity(entity: T): void {
        // this.log('Called view on entity ' + entity['id']);
        this.resource.navigate.toView(entity['id']);
    }

    editEntity(entity: T): void {
        // this.log('Called edit on entity ' + entity['id']);
        this.resource.navigate.toEdit(entity['id']);
    }

    deleteEntity(entity: T): void {
        // this.log('Called delete on entity ID ' + entity['id']);

        this._dialogService
            .showConfirm(
                {
                    message: `common.confirmDelete.message`,
                    title: `common.confirmation`,
                    confirmText: `common.yes`,
                    cancelText: `common.no`
                },
                {discriminator: this.resource.getEntityDiscriminator(entity)}
            )
            .then((dialogObservable) => {
                dialogObservable.subscribe((result) => {
                    if (result) {
                        this._store.dispatch(new DeleteEntity(this.resource, entity['id']));
                    }
                });
            });
    }

    deleteSelectedEntities(): void {
        const entityIds = this.getCurrentPageSelectedIds();
        this.confirmBulkDelete(entityIds).then((result) => {
            if (result) {
                // TODO: Create separate REST API for this later
                entityIds.forEach((id) => {
                    this._store.dispatch(new DeleteEntity(this.resource, id));
                });
            }
        });
    }

    confirmBulkDelete(ids: number[]): Promise<boolean> {
        return this._dialogService
            .showConfirm(
                {
                    message: `${this.resource.name}.messages.confirmDeleteAll.message`,
                    title: `${this.resource.name}.common.confirmation`,
                    confirmText: `${this.resource.name}.common.yes`,
                    cancelText: `${this.resource.name}.common.no`
                },
                {}
            )
            .then((dialogObservable) => dialogObservable.toPromise())
            .then((result) => result ?? false);
    }

    getEntityActions(): any[] {
        return [this.VIEW_ACTION, this.EDIT_ACTION];
    }

    getEntityGroupActions(): any {
        const groupActions = [];
        if (this.isDeleteSelected()) {
            groupActions.push(this.DELETE_SELECTED_ACTION);
        }

        if (this.isImportable()) {
            groupActions.push(this.IMPORT_CSV_ACTION);
        }
        if (this.isExportAll()) {
            groupActions.push(this.EXPORT_ALL);
            // tslint:disable-next-line
        }
        if (this.isExportAll()) {
            groupActions.push(this.EXPORT_SELECTED);
            // tslint:disable-next-line
        }
        if (this.isExportableToAccountingPackage()) {
            groupActions.push(this.EXPORT_SELECTED_TO_ACCOUNTING_PACKAGE);
        }

        if (AIResource[this.resource.name] && AIResource[this.resource.name].createOrUpdateEntity) {
            groupActions.push(this.IMPORT_PDF);
        }

        return groupActions;
    }

    getMasterCheckboxActions(): any {
        return [
            // Sample:
            // {
            //     label: 'trial',
            //     action: this.trialAction.bind(this)
            // }
        ];
    }

    getCurrentPageSelectedIds = (): number[] => {
        const listView = this._store.selectSnapshot((state) => state.entities[this.resourceName].listView);
        const entityList: any[] = listView.entityList;
        const selectedIds: number[] = listView.selectedIds;
        return entityList.map((entity) => entity.id).filter((id) => selectedIds.includes(id));
    };

    getAllPageSelectedIds = (): number[] => {
        const listView = this._store.selectSnapshot((state) => state.entities[this.resourceName].listView);
        const selectedIds: number[] = listView.selectedIds;
        return selectedIds;
    };

    /**
     * The first `limit` rows matching the list's current filters, search and sort - the rows the grid
     * shows, in the order it shows them, reaching past the page on screen. `total` is the number of
     * matching rows, so callers can tell when the limit cut the result short.
     */
    fetchSelectableEntities = (limit: number): Promise<{ids: number[]; entities: any[]; total: number}> => {
        const listView = this._store.selectSnapshot((state) => state.entities[this.resourceName].listView);
        const viewParams: ListViewParams = listView.viewParams;
        const api = viewParams.filter ? this.resource.api.searchHttp : this.resource.api.queryHttp;
        const params = buildListApiParams({...viewParams, pageIndex: 0, pageSize: limit}, listView.primaryFilter ?? this.getPrimaryFilter());

        return api(params).then((response) => {
            const entities = response.body || [];
            const total = parseInt(response.headers.get('X-Total-Count'), 10);
            return {
                ids: entities.map((entity) => entity['id']),
                entities,
                total: isNaN(total) ? entities.length : total
            };
        });
    };

    /**
     * Clears the selection on every page, not just the page currently being displayed.
     * Use this after a bulk action that operated on {@link getAllPageSelectedIds}.
     */
    clearAllPageSelectedIds = (): void => {
        const selectedIds = this.getAllPageSelectedIds();
        if (selectedIds.length > 0) {
            this._store.dispatch(new UnselectEntity(this.resource, selectedIds, null, this.resourceName));
        }
    };

    getMasterCheckboxStatus(): Observable<any> {
        return combineLatest(this.entityList$, this.selectedIds$, (latestEntityList, latestSelectedIds) => {
            let checked = false;
            let indeterminate = false;
            if (latestEntityList.length > 0) {
                const currentPageSelectedIds = latestEntityList.map((entity) => entity.id).filter((id) => latestSelectedIds.includes(id));
                if (currentPageSelectedIds.length > 0) {
                    checked = currentPageSelectedIds.length === latestEntityList.length;
                    indeterminate = currentPageSelectedIds.length < latestEntityList.length;
                }
            }
            return {checked, indeterminate};
        });
    }

    onMasterCheckboxChanged(checked: boolean): void {
        if (checked) {
            this._store.dispatch(new SelectAllCurrentPageEntities(this.resource, null, null, this.resourceName));
        } else {
            this._store.dispatch(new UnselectAllCurrentPageEntities(this.resource, null, null, this.resourceName));
        }
    }

    toggleEntitySelection = (entity: T, event: any): void => {
        if (event.checked) {
            this._store.dispatch(new SelectEntity(this.resource, [entity.id], null, this.resourceName));
        } else {
            this._store.dispatch(new UnselectEntity(this.resource, [entity.id], null, this.resourceName));
        }
    };

    private subscribeToStatusLoadingChangeFromState(): void {
        this._store
            .select((state) => state.entities[this.resourceName].listView.statusLoading)
            .subscribe((statusLoading) => {
                this.loading = statusLoading;
            });
    }

    private subscribeToViewParamsChangeFromState(): void {
        this.viewParams$.pipe(takeUntil(this._unsubscribeAll)).subscribe((viewParams) => {
            if (this.nzPagination) {
                this.setNzPaginationViewParams(viewParams);
            } else if (this.nzTable) {
                this.setNzViewParams(viewParams);
            }

            if (viewParams.filterParams) {
                this.loadFiltersIntoMap(viewParams.filterParams);
            }
        });
    }

    sortBy(sortName: string, value: any | NzSortDirection, defaulDirection?: any | NzSortDirection): void {
        try {
            this.log(`sortBy called. Starting state: ${this.sortName}, ${this.sortDir}`);
            this.sortName = value === null ? 'id' : sortName;
            if (!value && !defaulDirection) {
                this.sortDir = 'ascend';
            } else {
                this.sortDir = value === null && defaulDirection !== null ? defaulDirection : value;
            }
            this.log(`sortBy called. Ending state: ${this.sortName}, ${this.sortDir}`);
            for (const key of Object.keys(this.sortParams)) {
                this.sortParams[key] = key === sortName ? value : null;
            }
            this.sortParams[sortName] = value;
            this.log(`sortParams: ${JSON.stringify(this.sortParams)}`);
            // eslint-disable-next-line no-empty
        } catch (e) {}
    }

    private directNzPaginationViewParamsChangesToState(): void {
        const displayDataChanges = [
            this.nzPagination.nzPageSizeChange,
            this.nzPagination.nzPageIndexChange,
            this.nzHeader && this.nzHeader.search
                ? this.nzHeader.search.searchTerm$.pipe(
                      tap((searchTerm) => {
                          if (searchTerm) {
                              this.nzPagination.nzPageIndex = 1;
                              this.sortBy('id', 'descend');
                          }
                      })
                  )
                : of(null),
            this.filterParams$.pipe(
                tap((filterParams) => {
                    if (filterParams && filterParams.length !== 0) {
                        this.nzPagination.nzPageIndex = 1;
                    }
                })
            ),
            this.nzTable.nzQueryParams

            // deprecated
        ];

        merge<any>(...displayDataChanges)
            .pipe(takeUntil(this._unsubscribeAll), debounceTime(1000))
            .subscribe((changedData) => {
                this.pagingService.setPageSize(this.resourceName, this.nzPagination.nzPageSize);
                if (this.canUpdateListViewPage()) {
                    this.updateListViewPage();
                }
            });
    }

    canUpdateListViewPage(): boolean {
        return true;
    }

    updateListViewPage(): void {
        const reqData = {
            pageIndex: this.nzPagination.nzPageIndex - 1 || 0,
            pageSize: this.nzPagination.nzPageSize || this.pagingService.getPageSize(this.resourceName) || DEFAULT_PAGE_SIZE,
            sortColumn: this.sortName,
            sortDirection: 'ascend' === this.sortDir ? 'asc' : 'desc',
            filter: this.nzHeader && this.nzHeader.search ? this.nzHeader.search.getCurrentSearchTerm() || '' : '',
            filterParams: Object.values(this.filterParamMap),
            primaryFilter: this.getPrimaryFilter()
        } as ListViewParams;
        this._store.dispatch(new ChangeEntityListViewParams(this.resource, reqData, null, this.getPrimaryFilter(), this.resourceName));
    }

    private directNzViewParamsChangesToState(): void {
        const displayDataChanges = [
            this.nzTable.nzPageIndexChange,
            this.nzTable.nzPageSizeChange,
            this.nzHeader
                ? this.nzHeader.search.searchTerm$.pipe(
                      tap((searchTerm) => {
                          if (searchTerm) {
                              this.nzTable.nzPageIndex = 1;
                              this.sortBy('id', 'ascend');
                          }
                      })
                  )
                : of(null),
            this.nzTable.nzQueryParams,
            // deprecated
            this.filterParams$.pipe(
                tap((filterParams) => {
                    if (filterParams && filterParams.length !== 0 && this.nzPagination) {
                        this.nzPagination.nzPageIndex = 1;
                    }
                })
            )
        ];

        merge<any>(...displayDataChanges)
            .pipe(debounceTime(150), takeUntil(this._unsubscribeAll))
            .subscribe((changedData) => {
                this.log('There was a change of view params data');
                this.log(changedData);
                this.log('NZ Index', this.nzTable.nzPageIndex);
                this.log('NZ Page Size', this.nzTable.nzPageSize);
                this.pageIndex$.pipe(takeUntil(this._unsubscribeAll)).subscribe((pageIndex: number) => {
                    const reqData = {
                        pageIndex: pageIndex,
                        pageSize: this.nzTable.nzPageSize || this.pagingService.getPageSize(this.resourceName) || DEFAULT_PAGE_SIZE,
                        sortColumn: this.sortName,
                        sortDirection: 'ascend' === this.sortDir ? 'asc' : 'desc',
                        filter: this.nzHeader ? this.nzHeader.search.getCurrentSearchTerm() || '' : '',
                        filterParams: Object.values(this.filterParamMap),
                        primaryFilter: this.getPrimaryFilter()
                    } as ListViewParams;
                    this.log(`Change data ${JSON.stringify(reqData)}`);
                    this._store.dispatch(new ChangeEntityListViewParams(this.resource, reqData, null, this.getPrimaryFilter(), this.resourceName));
                });
            });
    }

    private getNzSortDirection(sortDir: string): string | null {
        if (sortDir) {
            return sortDir === 'asc' ? 'ascend' : 'descend';
        }
        return null;
    }

    private setNzPaginationViewParams(viewParams: ListViewParams): void {
        this.log('Setting view params from state', JSON.stringify(viewParams));
        if (this.nzPagination.nzPageIndex - 1 !== viewParams.pageIndex) {
            this.nzPagination.nzPageIndex = viewParams.pageIndex + 1;
            this.log(`Page index set to ${this.nzPagination.nzPageIndex}`);
        }
        if (this.nzPagination.nzPageSize !== viewParams.pageSize) {
            this.nzPagination.nzPageSize = viewParams.pageSize;
            this.log(`Page size set to ${this.nzPagination.nzPageSize}`);
        }
        let changed = false;
        let localSortName = null;
        let localSortDir = null;
        if (this.sortDir !== this.getNzSortDirection(viewParams.sortDirection)) {
            this.log(`Sort dir going to be set to ${viewParams.sortDirection}`);
            localSortDir = this.getNzSortDirection(viewParams.sortDirection);
            changed = true;
            // this.sortBy(this.sortName, this.getNzSortDirection(viewParams.sortDirection));
        }
        if (!((this.sortName === null || this.sortName === 'id') && viewParams.sortColumn === 'id') && this.sortName !== viewParams.sortColumn) {
            this.log(`Sort column going to be set to ${viewParams.sortColumn}`);
            changed = true;
            localSortName = viewParams.sortColumn;
            // this.sortBy(viewParams.sortColumn, this.sortDir);
        }
        if (changed) {
            this.sortBy(localSortName === null ? this.sortName : localSortName, localSortDir === null ? this.sortDir : localSortDir);
        }
        const currentSearchTerm = this.nzHeader.search.getCurrentSearchTerm();
        if (currentSearchTerm !== viewParams.filter) {
            this.nzHeader.search.setSearchTerm(viewParams.filter);
        }
    }

    private setNzViewParams(viewParams: ListViewParams): void {
        this.log('Setting view params from state', JSON.stringify(viewParams));
        if (this.nzTable.nzPageIndex - 1 !== viewParams.pageIndex) {
            this.nzTable.nzPageIndex = viewParams.pageIndex + 1;
            this.log(`Page index set to ${this.nzTable.nzPageIndex}`);
        }
        if (this.nzTable.nzPageSize !== viewParams.pageSize) {
            this.nzTable.nzPageSize = viewParams.pageSize;
            this.log(`Page size set to ${this.nzTable.nzPageSize}`);
        }
        let changed = false;
        let localSortName = null;
        let localSortDir = null;

        if (this.sortDir !== this.getNzSortDirection(viewParams.sortDirection)) {
            this.log(`Sort dir going to be set to ${viewParams.sortDirection}`);
            localSortDir = this.getNzSortDirection(viewParams.sortDirection);
            changed = true;
            // this.sortBy(this.sortName, this.getNzSortDirection(viewParams.sortDirection));
        }
        if (!((this.sortName === null || this.sortName === 'id') && viewParams.sortColumn === 'id') && this.sortName !== viewParams.sortColumn) {
            this.log(`Sort column going to be set to ${viewParams.sortColumn}`);
            changed = true;
            localSortName = viewParams.sortColumn;
            // this.sortBy(viewParams.sortColumn, this.sortDir);
        }
        if (changed) {
            this.sortBy(localSortName === null ? this.sortName : localSortName, localSortDir === null ? this.sortDir : localSortDir);
        }
        if (this.nzHeader.search.getCurrentSearchTerm() !== viewParams.filter) {
            this.nzHeader.search.setSearchTerm(viewParams.filter);
        }
    }

    changeExportSettings(): void {
        this._modalService.create({
            nzContent: ExportSettingsModalComponent,
            nzData: this.getExportSettingsModalData(),
            nzWidth: 1050,
            nzStyle: {top: '20px'},
            nzBodyStyle: {
                maxHeight: '638px'
            },
            nzClosable: false,
            nzFooter: null
        });
    }

    formatDateString(date: string | DiDate): string {
        return DiDate.inUKDateFormat(date as string);
    }

    sortColumn($event): void {
        this.log('Sort', JSON.stringify($event));
    }

    log(...logStr: any): void {
        if (DEBUG_PAGE_PARAMS) {
            console.log(...logStr);
        }
    }

    getExportName(): string {
        return null;
    }

    getExportSettingsModalData(): ExportSettingsModalData {
        return {exportType: this.getExportName()};
    }

    /** Null exports from `api/<exportType>/export-all`. */
    getExportAllUrl(): string | null {
        return null;
    }

    getExportType(): string {
        return '';
    }

    isExportAll(): boolean {
        return false;
    }

    isDeleteSelected(): boolean {
        return true;
    }

    exportAll(): void {
        const count = this.exportCount;
        const exportType = this.exportType;
        const obj = {};
        this.filterParams$.pipe().subscribe((data) => {
            data.forEach((filter) => {
                obj[filter.columnName] = filter.currentValue instanceof Array ? filter.currentValue.join() : filter.currentValue;
            });
        });
        obj['count'] = count;
        this._groupActionSubject.next(true);
        this._exportService.exportAll(obj, exportType, this.getExportAllUrl()).then(() => {
            this._groupActionSubject.next(false);
        });
    }

    exportSelected(): void {
        this.selectedIds$.pipe().subscribe((data) => {
            this.selectedIds = data;
        });
        if (this.selectedIds.length === 0) {
            this._dialogService.showMessage({
                message: `${this.resource.name}.messages.noSelectedOrder.message`,
                title: `${this.resource.name}.messages.noSelectedOrder.title`,
                confirmText: `${this.resource.name}.common.yes`
            });
            return;
        }
        this._groupActionSubject.next(true);
        this._exportService.exportSelected(this.selectedIds, this.exportType).then(() => {
            this._groupActionSubject.next(false);
        });
    }

    importCsv(): void {
        this._groupActionSubject.next(true);
        this.importer.openModal();
    }

    isImportable(): boolean {
        return false;
    }

    protected getImportEntity(): ImportEntity | null {
        return null;
    }

    protected onCsvUploadComplete(data, flags): void {
        const importEntity = this.getImportEntity();
        if (importEntity && data && data.rows) {
            this.httpClient
                .post<any>(SERVER_API_URL + `api/import/${importEntity}/json`, {
                    dataRows: data.rows,
                    importFlags: flags
                })
                .subscribe(
                    (suc) => {
                        console.log(suc);
                        this.onImportComplete(this.convertCsvImportResultToStandardFormat(importEntity, suc));
                        this.initializeFilterData();
                    },
                    (err) => {
                        console.log(err);
                    }
                );
        }
    }

    private convertCsvImportResultToStandardFormat(entity: string, result: any): any {
        const standardFormat = {};
        standardFormat[entity.toUpperCase()] = {
            categoryFailed: false,
            data: [],
            report: result
        };
        return standardFormat;
    }

    protected onImportComplete(importResult: any) {
        if (importResult && this._modalService) {
            const modalRef = this._modalService.create({
                nzContent: ImportCsvResultModalComponent,
                nzData: {
                    importResult: importResult
                },
                nzStyle: {
                    width: '50%'
                },
                nzFooter: null
            });
            modalRef.afterClose.subscribe(() => {
                this._groupActionSubject.next(false);
                this.loadEntityList();
            });
        }
    }

    getRequestFilters() {
        let viewParams = this._store.selectSnapshot<any>((state) => state.entities[this.resource.name].listView.viewParams);

        const returnValue = {
            page: viewParams.pageIndex,
            size: viewParams.pageSize,
            sort: viewParams.sortColumn + ',' + viewParams.sortDirection,
            query: viewParams.filter
        };

        const filters = viewParams.filterParams;
        if (filters) {
            for (const filter of filters) {
                if (filter.currentValue) {
                    returnValue[filter.columnName] = filter.currentValue.toString();
                }
            }
        }

        if (this.getPrimaryFilter()) {
            returnValue['primaryFilter'] = this.getPrimaryFilter();
        }

        return returnValue;
    }

    isExportableToAccountingPackage(): boolean {
        return false;
    }

    exportSelectedToAccountingPackage(getCurrentPageId: boolean, selectedIds: any): void {
        const ids = getCurrentPageId ? this.getCurrentPageSelectedIds() : selectedIds;
        this._groupActionSubject.next(true);
        this.resource.api
            .exportToAccountingPackage(ids)
            .then((result) => {
                if (result != null) {
                    this.showImportResultModal(result);
                } else {
                    this._alertService.showWarning(
                        'Though Accounting package is not connected, we have created the invoice(s). Please ignore, if accounting package is not integrated.'
                    );
                }
            })
            .finally(() => {
                this._groupActionSubject.next(false);
                // Untick the posted rows so they aren't still selected for a second post. Unselects the exact
                // ids rather than calling onMasterCheckboxChanged, which some lists (e.g. invoices) override.
                this._store.dispatch(new UnselectEntity(this.resource, ids, null, this.resourceName));
            });
    }

    private showImportResultModal(importResult: ImportResult): void {
        if (!this._modalService) {
            console.error('Modal service not available. Cannot show import result modal.');
            return;
        }
        this._modalService.create({
            nzContent: ImportCsvResultModalComponent,
            nzData: {
                importResult: importResult
            },
            nzStyle: {
                width: '50%'
            },
            nzFooter: null
        });
    }

    importPdf(): void {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '*'; // Adjust file types as needed
        fileInput.onchange = (event: any) => {
            const file = event.target.files[0];
            if (file) {
                this.aIInputService.aiLoading.next(true);
                this.aIInputService.processCommands('Create(' + this.resource.name + ')', file, ['createOrUpdateEntity'], this.resource.name);
            }
        };
        fileInput.click();
    }
}
